import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage, injectIntl, type WrappedComponentProps } from 'react-intl-next';

import { withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { Field } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';

import { validateTeamName } from '../../utils/validators';
import { messages } from '../i18n';
import { TeamNameErrorMsg } from '../TeamCreateDialog/TeamNameVisibilityWarning';
import { ValidationError } from '../TeamCreateDialog/types';

interface Props {
	onSubmit: () => void;
	onChange: (teamName: string) => void;
	isSubmittedOnce: boolean;
	initialTeamName?: string;
}

function TeamNameInput(props: Props & WrappedComponentProps & WithAnalyticsEventsProps) {
	const { intl, onSubmit, onChange, isSubmittedOnce, initialTeamName } = props;
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current && inputRef.current.focus();
	}, []);

	const [name, setName] = useState(initialTeamName);

	const handleTextChange = useCallback(
		(value?: string): undefined | ValidationError => {
			setName(value || '');
			const teamNameValidationError = validateTeamName(value);
			if (isSubmittedOnce && teamNameValidationError === ValidationError.REQUIRED) {
				return teamNameValidationError;
			}

			if (teamNameValidationError === ValidationError.EXCEED_MAX_LENGTH) {
				return teamNameValidationError;
			}

			onChange(value?.trim() || '');
			return undefined;
		},
		[onChange, isSubmittedOnce],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>): void => {
			if (event.key === 'Enter') {
				event.stopPropagation();
				onSubmit();
			}
		},
		[onSubmit],
	);

	const getErrorMsg = (error: string) => {
		if (error === ValidationError.REQUIRED) {
			return intl.formatMessage(messages.teamNameFieldRequiredErrorMessage);
		}

		return intl.formatMessage(messages.teamNameFieldTooLongErrorMessage);
	};

	return (
		<Field
			name="teamName"
			label={<FormattedMessage {...messages.teamNameFieldLabelNext} />}
			isRequired
			validate={handleTextChange}
			defaultValue={initialTeamName}
		>
			{({ fieldProps, error }) => (
				<>
					<Textfield
						{...fieldProps}
						autoComplete="off"
						isRequired
						isDisabled={false}
						name="teamName"
						onKeyDown={handleKeyDown}
						testId="team-name-input"
						aria-labelledby=""
						ref={inputRef}
						value={name}
					/>
					{error ? <TeamNameErrorMsg errorMsg={getErrorMsg(error)} /> : null}
				</>
			)}
		</Field>
	);
}

export default withAnalyticsEvents()(injectIntl(TeamNameInput));
